import { useMemo } from 'react'
import { Check } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { DEFAULT_LABEL_PREFIX, gridCellLabels } from '@/lib/slotLabels'
import type { SlotLabelScheme, StorageNodeKind } from '@/types/storage'

/**
 * Choose how a grid names its cells.
 *
 * A dropdown reading "A1 … D5" made you imagine the result; this shows it. Each
 * row previews the labels THIS grid would actually mint — same function the
 * server uses — so a 3×3 shows `A1 A2 A3` rather than a generic example that
 * may not resemble what you get.
 *
 * The two schemes are not interchangeable style choices: row/column is the
 * SLAS convention printed on cryoboxes, and sequential is what the design
 * note's own addresses use. Saying what each is FOR is the part that lets
 * someone pick correctly without knowing either convention.
 */

const SCHEMES: {
  value: SlotLabelScheme
  title: string
  bestFor: string
}[] = [
  {
    value: 'ROW_COLUMN',
    title: 'Row & column',
    bestFor: 'Cryoboxes and plates — matches the grid printed on the box.',
  },
  {
    value: 'SEQUENTIAL',
    title: 'Numbered in order',
    bestFor: 'Racks, shelves and positions counted straight through.',
  },
]

export function LabelSchemePicker({
  rows,
  columns,
  childKind,
  scheme,
  prefix,
  onChange,
}: {
  rows: number
  columns: number
  childKind: StorageNodeKind
  scheme: SlotLabelScheme
  prefix?: string
  onChange: (next: { scheme: SlotLabelScheme; prefix?: string }) => void
}) {
  const fallbackPrefix = DEFAULT_LABEL_PREFIX[childKind] ?? 'N'
  const effPrefix = prefix || fallbackPrefix

  const previews = useMemo(() => {
    const result: Record<SlotLabelScheme, { sample: string[]; range: string | null }> = {
      ROW_COLUMN: { sample: [], range: null },
      SEQUENTIAL: { sample: [], range: null },
    }
    for (const value of ['ROW_COLUMN', 'SEQUENTIAL'] as SlotLabelScheme[]) {
      try {
        const cells = gridCellLabels({
          rows,
          columns,
          scheme: value,
          prefix: effPrefix,
        })
        result[value] = {
          sample: cells.slice(0, 5).map((c) => c.code),
          range: cells.length > 5 ? `${cells[0].code} … ${cells[cells.length - 1].code}` : null,
        }
      } catch {
        // keep defaults
      }
    }
    return result
  }, [rows, columns, effPrefix])

  const select = (value: SlotLabelScheme) =>
    onChange({
      scheme: value,
      prefix: value === 'SEQUENTIAL' ? effPrefix : undefined,
    })

  return (
    <div className='space-y-2'>
      {/* Two choices, so two rows — not a table with a header. Each shows the
          codes THIS grid would mint, which is the whole argument for picking
          one over the other. */}
      <div role='radiogroup' aria-label='How the slots are named' className='space-y-2'>
        {SCHEMES.map((option) => {
          const active = scheme === option.value
          const { sample, range } = previews[option.value]

          return (
            <button
              key={option.value}
              type='button'
              role='radio'
              aria-checked={active}
              onClick={() => select(option.value)}
              className={cn(
                'flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
                active
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground/30 hover:bg-muted/40',
              )}
            >
              <span
                className={cn(
                  'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                  active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-muted-foreground/40',
                )}
              >
                {active && <Check className='h-3 w-3' />}
              </span>

              <span className='min-w-0 flex-1 space-y-1.5'>
                <span className='flex flex-wrap items-baseline gap-x-2 gap-y-1'>
                  <span className='text-sm font-medium'>{option.title}</span>
                  <span className='text-xs text-muted-foreground'>{option.bestFor}</span>
                </span>

                <span className='flex flex-wrap items-center gap-1'>
                  {sample.map((code) => (
                    <span
                      key={code}
                      className={cn(
                        'rounded border px-1.5 py-0.5 font-mono text-[11px]',
                        active
                          ? 'border-primary/30 bg-primary/10 text-primary'
                          : 'border-border bg-muted/40 text-muted-foreground',
                      )}
                    >
                      {code}
                    </span>
                  ))}
                  {sample.length === 0 && <span className='text-xs text-muted-foreground'>—</span>}
                  {range && <span className='text-[11px] text-muted-foreground'>{range}</span>}
                </span>
              </span>
            </button>
          )
        })}
      </div>

      {scheme === 'SEQUENTIAL' && (
        <div className='flex flex-wrap items-center gap-3 pt-1'>
          <Label htmlFor='label-prefix' className='text-xs font-normal text-muted-foreground'>
            Letter in front
          </Label>
          <Input
            id='label-prefix'
            className='h-8 w-20'
            value={prefix ?? ''}
            placeholder={fallbackPrefix}
            onChange={(e) => onChange({ scheme, prefix: e.target.value })}
          />
          <p className='text-[11px] text-muted-foreground'>
            Numbers are padded to the width of the total, so they sort correctly — ten cells give{' '}
            {effPrefix}01…{effPrefix}10.
          </p>
        </div>
      )}
    </div>
  )
}
